import React, { useCallback, useEffect } from "react";

import { faMemoPad } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";

import Button, { LabelIconForButton } from "~/ui/button";

import type { TranscriptTabType } from "./TranscriptTabs";
import { useShouldShowRecapTab } from "./useShouldShowRecapTab";

import type { useShouldShowRecapTab_note$key } from "~/__generated__/useShouldShowRecapTab_note.graphql";

import styles from "./MeetingTabs.module.css";

export type MeetingTabType = "agenda" | "recap";

type MeetingTabsProps = {
	currentNotesTab: MeetingTabType;
	currentCombinedGroupTab: MeetingTabType | TranscriptTabType;
	setCurrentTab: (tab: MeetingTabType | TranscriptTabType) => void;
	noteKey: useShouldShowRecapTab_note$key | null;
	showClipsTab: boolean;
	isNarrow: boolean;
};

export default function MeetingTabs({
	currentNotesTab,
	currentCombinedGroupTab,
	setCurrentTab,
	noteKey,
	showClipsTab,
	isNarrow,
}: MeetingTabsProps) {
	const onAgendaClick = useCallback(() => setCurrentTab("agenda"), [setCurrentTab]);
	const onRecapClick = useCallback(() => setCurrentTab("recap"), [setCurrentTab]);
	const onTranscriptClick = useCallback(() => setCurrentTab("Transcript"), [setCurrentTab]);
	const onClipsClick = useCallback(() => setCurrentTab("Clips"), [setCurrentTab]);

	const showRecapTab = useShouldShowRecapTab(noteKey);

	useEffect(() => {
		if (!showRecapTab) {
			setCurrentTab("agenda");
		}
	}, [setCurrentTab, showRecapTab]);

	if (!showRecapTab) {
		console.error("Not yet implemented.");
		return null; // This isn't ideal but we can't call the hook in the NoteCard parent class
		// TODO: Which hook are they talking about above? -Wal
	}

	if (!isNarrow) {
		return (
			<div className={styles.tabsWrapper}>
				<Button
					variant="tertiary"
					size="normal"
					className={cx(styles.tab, { [styles.active]: currentNotesTab === "agenda" })}
					onClick={onAgendaClick}
					aria-label="Agenda"
				>
					<LabelIconForButton side="left" icon={faMemoPad} />
					Agenda
				</Button>

				<Button
					variant="tertiary"
					size="normal"
					className={cx(styles.tab, { [styles.active]: currentNotesTab === "recap" })}
					onClick={onRecapClick}
					data-tour-tooltip-target="recap-tab"
					aria-label="AI Note"
				>
					<LabelIconForButton side="left" icon={faSparkles} />
					AI Note
				</Button>
			</div>
		);
	} else {
		return (
			<div className={styles.combinedTabs}>
				<Button
					variant="tertiary"
					size="normal"
					className={cx(styles.tab, { [styles.active]: currentCombinedGroupTab === "agenda" })}
					onClick={onAgendaClick}
				>
					Agenda
				</Button>
				<Button
					variant="tertiary"
					size="normal"
					className={cx(styles.tab, { [styles.active]: currentCombinedGroupTab === "recap" })}
					onClick={onRecapClick}
				>
					AI Note
				</Button>
				<Button
					variant="tertiary"
					size="normal"
					className={cx(styles.tab, { [styles.active]: currentCombinedGroupTab === "Transcript" })}
					onClick={onTranscriptClick}
				>
					Transcript
				</Button>
				{showClipsTab && (
					<Button
						variant="tertiary"
						size="normal"
						className={cx(styles.tab, { [styles.active]: currentCombinedGroupTab === "Clips" })}
						onClick={onClipsClick}
					>
						Clips
					</Button>
				)}
			</div>
		);
	}
}
